/**
 * SpreadsheetSourceStep — Step 1 of the SpreadsheetConnectDialog.
 *
 * Two render branches:
 *   - ``upload``: drag&drop dropzone + hidden ``<input type="file">``.
 *   - cloud (``google_drive`` / ``onedrive``): optional multi-account
 *     chip selector + provider-specific CTA that opens the native picker.
 *
 * Owned by ``SpreadsheetConnectDialog`` — receives all state via props,
 * has no internal state of its own (dropzone drag-over highlight is
 * lifted to the parent because the dialog's reset effect needs to clear
 * it on close).
 */

import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowRight, Cloud, FileSpreadsheet, Loader2, RefreshCw } from "lucide-react";
import type * as React from "react";

import { type BaseConnection, Button, type CapabilityWizardSpec, cn } from "@ando/ui";

import type { ConnectionSummary } from "@ando/core";

import type { SpreadsheetSourceMode } from "../../wizards/spreadsheetSpec";
import { InlineErrorBanner } from "../InlineErrorBanner";
import { VendorBrandBadge } from "../VendorBrandBadge";
import { SpreadsheetFileDropzone } from "./SpreadsheetFileDropzone";
import { type CloudFileSelection, SOURCE_PROVIDER_LABEL, formatBytes } from "./types";

export interface SpreadsheetSourceStepProps {
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  readonly sourceMode: SpreadsheetSourceMode;
  readonly cloudConnections: readonly ConnectionSummary[];
  readonly selectedConnectionKey: string | null;
  readonly onPickConnection: (key: string | null) => void;
  readonly previewLoading: boolean;
  readonly error: string | null;
  readonly file: File | null;
  readonly cloudFile: CloudFileSelection | null;
  readonly dragOver: boolean;
  readonly fileInputRef: React.RefObject<HTMLInputElement | null>;
  readonly onDragOver: (e: React.DragEvent<HTMLElement>) => void;
  readonly onDragLeave: (e: React.DragEvent<HTMLElement>) => void;
  readonly onDrop: (e: React.DragEvent<HTMLElement>) => void;
  readonly onDropzoneClick: () => void;
  readonly onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onOpenCloudPicker: () => void;
  readonly pickerAvailable: boolean;
}

export const SpreadsheetSourceStep: React.FC<SpreadsheetSourceStepProps> = ({
  spec,
  sourceMode,
  cloudConnections,
  selectedConnectionKey,
  onPickConnection,
  previewLoading,
  error,
  file,
  cloudFile,
  dragOver,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onDropzoneClick,
  onFileChange,
  onOpenCloudPicker,
  pickerAvailable,
}) => {
  const { t } = useLingui();
  if (sourceMode === "upload") {
    return (
      <div>
        <div className="mt-4 flex items-start gap-3">
          <VendorBrandBadge vendorId="upload" fallbackIcon={spec.icon} size="lg" />
          <p className="text-[12.5px] text-ink-2 leading-relaxed">
            <Trans>
              Carica un file Excel (.xlsx / .xls) o CSV dal tuo computer. Il file viene cifrato a
              riposo; revoca disponibile dalla pagina Integrazioni.
            </Trans>
          </p>
        </div>

        <div className="mt-4">
          <SpreadsheetFileDropzone
            file={file}
            busy={previewLoading}
            dragOver={dragOver}
            fileInputRef={fileInputRef}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onPick={onDropzoneClick}
            onFileChange={onFileChange}
          />
        </div>

        <InlineErrorBanner message={error} />
      </div>
    );
  }

  // ── Cloud modes (google_drive / onedrive) ─────────────────────────
  const cloudProvider = sourceMode as "google_drive" | "onedrive";
  const providerLabel = SOURCE_PROVIDER_LABEL[cloudProvider];
  // Public provider rides ``provider`` (BE); ``vendor`` is the enterprise
  // fallback — the raw ``vendor`` is undefined for white-label drive rows.
  const filtered = cloudConnections.filter((c) => (c.provider ?? c.vendor) === cloudProvider);
  const showAccountChips = filtered.length > 1;
  return (
    <div>
      <div className="mt-4 flex items-start gap-3">
        <VendorBrandBadge vendorId={sourceMode} fallbackIcon={spec.icon} size="lg" />
        <p className="text-[12.5px] text-ink-2 leading-relaxed">
          <Trans>
            Scegli un file sul tuo account {providerLabel} collegato. Ando legge solo i file che
            selezioni.
          </Trans>
        </p>
      </div>

      {showAccountChips ? (
        <fieldset className="mt-4">
          <legend className="font-medium text-[11.5px] text-ink-2">
            <Trans>Account {providerLabel}</Trans>
          </legend>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {filtered.map((c) => {
              const k = c.connection_key ?? c.conn_id;
              const isSelected = selectedConnectionKey === c.connection_key;
              return (
                <button
                  key={c.conn_id}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => onPickConnection(c.connection_key ?? null)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12px] transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                    isSelected
                      ? "border-ando-orange bg-ando-orange-soft/40 text-ink-1"
                      : "border-hairline bg-white/60 text-ink-2 hover:border-ando-orange/40",
                  )}
                  data-testid={`drive-account-chip-${k}`}
                >
                  <Cloud className="h-3.5 w-3.5" aria-hidden />
                  <span className="max-w-[160px] truncate">{c.connection_key ?? c.conn_id}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <div className="mt-4 flex flex-col items-center gap-3 rounded-lg border border-hairline bg-white/40 px-4 py-6 text-center">
        {previewLoading ? (
          <>
            <Loader2 className="h-7 w-7 animate-spin text-ando-orange" aria-hidden />
            <p className="text-[12.5px] text-ink-2">
              <Trans>Scarico l'anteprima dal cloud…</Trans>
            </p>
          </>
        ) : cloudFile ? (
          <>
            <FileSpreadsheet className="h-7 w-7 text-ando-orange" aria-hidden />
            <div>
              <p className="font-medium text-[13px] text-ink-1">{cloudFile.name}</p>
              {cloudFile.size_bytes !== undefined && cloudFile.size_bytes > 0 ? (
                <p className="mt-0.5 text-[11.5px] text-ink-3">
                  {formatBytes(cloudFile.size_bytes)}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={onOpenCloudPicker}
              disabled={!pickerAvailable}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              <Trans>Cambia file</Trans>
            </Button>
          </>
        ) : (
          <>
            <Cloud className="h-7 w-7 text-ink-3" aria-hidden />
            <p className="text-[12.5px] text-ink-2">
              <Trans>Apri il picker per scegliere un foglio da {providerLabel}.</Trans>
            </p>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={onOpenCloudPicker}
              disabled={!pickerAvailable}
              data-testid="open-cloud-picker"
            >
              {cloudProvider === "google_drive" ? t`Apri Google Picker` : t`Sfoglia OneDrive`}
              <ArrowRight aria-hidden />
            </Button>
            {!pickerAvailable ? (
              <p className="text-[11px] text-ink-3">
                <Trans>Picker non disponibile in questo contesto.</Trans>
              </p>
            ) : null}
          </>
        )}
      </div>

      <InlineErrorBanner message={error} />
    </div>
  );
};

SpreadsheetSourceStep.displayName = "SpreadsheetSourceStep";
